import { VideoData } from '@lumen5/luminary';

import { resolveThemeRefString } from 'js/src/libs/theme-ref-palette';

type AnyRecord = Record<string, unknown>;

export type ChartDisplayedColor = {
    /** The original color reference in the config, e.g. `themeRef_colors_3` or `#ff0000`. */
    ref: string;
    /** Resolved hex color for display. */
    hex: string;
};

function parseThemeColorIndex(value: unknown): number | null {
    if (typeof value !== 'string') return null;
    if (!VideoData.isTemplateReference(value)) return null;
    if (!value.includes('_colors_')) return null;
    const idx = VideoData.getTemplateIndex(value);
    return Number.isInteger(idx) && idx >= 0 ? idx : null;
}

function walkColorStrings(node: unknown, visit: (value: string) => void): void {
    if (typeof node === 'string') {
        visit(node);
        return;
    }
    if (!node || typeof node !== 'object') return;
    if ('$animate' in (node as AnyRecord)) return;
    if (Array.isArray(node)) {
        for (const child of node) walkColorStrings(child, visit);
        return;
    }
    for (const key of Object.keys(node as AnyRecord)) {
        walkColorStrings((node as AnyRecord)[key], visit);
    }
}

function collectUsedThemeRefIndices(echartsConfig: AnyRecord): number[] {
    const set = new Set<number>();
    walkColorStrings(echartsConfig, (value) => {
        const idx = parseThemeColorIndex(value);
        if (idx !== null) set.add(idx);
    });
    return Array.from(set).sort((a, b) => a - b);
}

type ColorGroup = 'visualization' | 'text';

function classifyColorGroup(path: string[]): ColorGroup {
    for (let i = path.length - 1; i >= 0; i--) {
        const key = path[i];
        if (key === 'itemStyle' || key === 'lineStyle' || key === 'areaStyle') {
            return 'visualization';
        }
        if (
            key === 'label' ||
            key === 'endLabel' ||
            key === 'textStyle' ||
            key === 'subtextStyle' ||
            key === 'axisLabel' ||
            key === 'axisName'
        ) {
            return 'text';
        }
        // graphic[*] is overwhelmingly text in our chart configs (captions, highlight
        // numbers). If we ever add non-text graphics with themeRef fills, this rule will
        // need to inspect graphic.children[*].type instead.
        if (key === 'graphic') {
            return 'text';
        }
    }
    return 'visualization';
}

function walkColorStringsWithPath(
    node: unknown,
    visit: (value: string, path: string[]) => void,
    path: string[] = [],
): void {
    if (typeof node === 'string') {
        visit(node, path);
        return;
    }
    if (!node || typeof node !== 'object') return;
    if ('$animate' in (node as AnyRecord)) return;
    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
            path.push(String(i));
            walkColorStringsWithPath(node[i], visit, path);
            path.pop();
        }
        return;
    }
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        walkColorStringsWithPath((node as AnyRecord)[key], visit, path);
        path.pop();
    }
}

function remapChartColors(node: unknown, mapping: Map<string, string>): unknown {
    if (typeof node === 'string') {
        return mapping.get(node) ?? node;
    }
    if (!node || typeof node !== 'object') return node;
    if ('$animate' in (node as AnyRecord)) return node;
    if (Array.isArray(node)) {
        return node.map((child) => remapChartColors(child, mapping));
    }
    const result: AnyRecord = {};
    for (const key of Object.keys(node as AnyRecord)) {
        result[key] = remapChartColors((node as AnyRecord)[key], mapping);
    }
    return result;
}

/**
 * Returns one entry per distinct template color reference found anywhere in the echarts
 * config. Count of entries equals the count of distinct palette slots the chart pulls from.
 */
export function getChartDisplayedColors(
    echartsConfig: AnyRecord,
    templateColors: number[],
): ChartDisplayedColor[] {
    const indices = collectUsedThemeRefIndices(echartsConfig);
    return indices.map((i) => {
        const ref = VideoData.templateColorByIndex(i);
        return { ref, hex: resolveThemeRefString(ref, templateColors) };
    });
}

/**
 * Remap every occurrence of `previousRef` in the config to `newColor`. If the previous value
 * was a template color reference and the user picks a hex, that slot in the chart becomes a
 * fixed hex override and no longer follows template palette changes.
 */
export function setChartPaletteColor(
    echartsConfig: AnyRecord,
    previousRef: string,
    newColor: string,
): AnyRecord {
    if (!previousRef || previousRef === newColor) return echartsConfig;
    return remapChartColors(echartsConfig, new Map([[previousRef, newColor]])) as AnyRecord;
}

function fisherYatesInPlace<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
}

/**
 * Pick a permutation of `used` indices that prefers `notUsed` slots first, falling back to
 * permuting among `used` when the chart already pulls from every template slot. Returns a
 * Map from old palette index → new palette index. When the only viable result is an
 * identity mapping (single-color group + no other slots available), returns an empty map
 * so callers can no-op.
 */
function buildGroupShuffleMapping(used: number[], templateColorCount: number): Map<number, number> {
    if (used.length === 0) return new Map();

    const usedSet = new Set(used);
    const allIndices = Array.from({ length: templateColorCount }, (_, i) => i);
    const notUsed = allIndices.filter((i) => !usedSet.has(i));
    const alsoUsed = allIndices.filter((i) => usedSet.has(i));

    for (let attempt = 0; attempt < 8; attempt++) {
        const shuffledNotUsed = [...notUsed];
        const shuffledUsed = [...alsoUsed];
        fisherYatesInPlace(shuffledNotUsed);
        fisherYatesInPlace(shuffledUsed);
        const pool = [...shuffledNotUsed, ...shuffledUsed].slice(0, used.length);

        const candidate = new Map<number, number>();
        let anyChanged = false;
        for (let i = 0; i < used.length; i++) {
            const from = used[i];
            const to = pool[i];
            candidate.set(from, to);
            if (from !== to) anyChanged = true;
        }
        if (anyChanged) return candidate;
    }
    return new Map();
}

function remapColorsByGroup(
    node: unknown,
    mappingByGroup: Record<ColorGroup, Map<number, number>>,
    path: string[],
): unknown {
    if (typeof node === 'string') {
        const idx = parseThemeColorIndex(node);
        if (idx === null) return node;
        const group = classifyColorGroup(path);
        const newIdx = mappingByGroup[group].get(idx);
        if (newIdx === undefined || newIdx === idx) return node;
        return VideoData.templateColorByIndex(newIdx);
    }
    if (!node || typeof node !== 'object') return node;
    if ('$animate' in (node as AnyRecord)) return node;
    if (Array.isArray(node)) {
        return node.map((child, i) => {
            path.push(String(i));
            const result = remapColorsByGroup(child, mappingByGroup, path);
            path.pop();
            return result;
        });
    }
    const result: AnyRecord = {};
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        result[key] = remapColorsByGroup((node as AnyRecord)[key], mappingByGroup, path);
        path.pop();
    }
    return result;
}

export function shuffleChartPalette(
    echartsConfig: AnyRecord,
    templateColorCount: number,
): AnyRecord {
    if (templateColorCount <= 0) return echartsConfig;

    const usedByGroup: Record<ColorGroup, Set<number>> = {
        visualization: new Set(),
        text: new Set(),
    };

    walkColorStringsWithPath(echartsConfig, (value, path) => {
        const idx = parseThemeColorIndex(value);
        if (idx === null) return;
        usedByGroup[classifyColorGroup(path)].add(idx);
    });

    if (usedByGroup.visualization.size === 0 && usedByGroup.text.size === 0) {
        return echartsConfig;
    }

    const mappingByGroup: Record<ColorGroup, Map<number, number>> = {
        visualization: buildGroupShuffleMapping(
            Array.from(usedByGroup.visualization).sort((a, b) => a - b),
            templateColorCount,
        ),
        text: buildGroupShuffleMapping(
            Array.from(usedByGroup.text).sort((a, b) => a - b),
            templateColorCount,
        ),
    };

    const anyChanged =
        Array.from(mappingByGroup.visualization.entries()).some(([k, v]) => k !== v) ||
        Array.from(mappingByGroup.text.entries()).some(([k, v]) => k !== v);
    if (!anyChanged) return echartsConfig;

    return remapColorsByGroup(echartsConfig, mappingByGroup, []) as AnyRecord;
}
